import { useCallback, useEffect, useState } from 'react';
import {
  useParams,
  useLocation,
  useNavigate,
  generatePath,
  useMatch,
} from 'react-router-dom';
import { BaseTransaction, NostroSidepanel } from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearTransactionState } from '../store/slices/exchange-transaction';
import {
  useLazyGetCustomersTransactionLogsQuery,
  useLazyGetCustomerNostroTransactionQuery,
} from '../store/services/customers';

const ProblemNostroTransactionHOC = ({
  backPath,
  generalPath,
  logPath,
  matchPath,
  changeLogPath,
  commentsPath,
}: {
  backPath: string;
  generalPath: string;
  logPath: string;
  matchPath: string;
  changeLogPath: string;
  commentsPath: string;
}): React.JSX.Element => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const {
    customersProblemTransactions: { nostro },
    customersDictionaries: { currenciesList },
  } = useAppSelector((state) => state);

  const { customerId, transactionId } = useParams();

  const [getNostroLogs, { isLoading: logsIsLoading }] =
    useLazyGetCustomersTransactionLogsQuery();

  const [getCustomerNostroTransaction, { isLoading, isFetching }] =
    useLazyGetCustomerNostroTransactionQuery();

  const getNostroTransactionById = useCallback(() => {
    getCustomerNostroTransaction(transactionId as string);
    getNostroLogs(transactionId as string);
  }, [transactionId]);

  const { search } = useLocation();
  const navigate = useNavigate();
  const match = useMatch(matchPath);

  const dispatch = useAppDispatch();

  const handleClose = () => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(
        generatePath(`${backPath}${search || ''}`, {
          customerId,
        }),
      );
    }, 100);
  };

  useEffect(() => {
    setPanelIsOpen(true);

    return () => {
      dispatch(clearTransactionState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (customerId && !nostro?.transaction && transactionId) {
      getNostroTransactionById();
    }
  }, [
    getNostroTransactionById,
    customerId,
    nostro?.transaction,
    transactionId,
  ]);

  useEffect(() => {
    if (match && customerId && transactionId) {
      navigate(
        generatePath(`${generalPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
      );
    }
  }, [match, customerId, transactionId]);

  return (
    <NostroSidepanel
      panelIsOpen={panelIsOpen}
      handleClose={handleClose}
      transactionInfo={nostro as BaseTransaction}
      tabs={{
        generalPath: generatePath(`${generalPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
        logPath: generatePath(`${logPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
        changeLogPath: generatePath(`${changeLogPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
        commentsPath: generatePath(`${commentsPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
      }}
      isLoading={logsIsLoading || isLoading || isFetching}
      title="Nostro Transaction"
      currenciesList={currenciesList}
      sidepanelExpandedKey="problem-nostro"
    />
  );
};

export default ProblemNostroTransactionHOC;
